/**
 * useReflectionPrompt — Contextual Prompt Selection Hook
 *
 * Selects the best reflection prompt based on user context:
 * time of day, recency rotation, category balance, people staleness,
 * task completions, gap detection, and depth escalation.
 *
 * Core selection logic is in the pure `selectPrompt` function for testability.
 */

import { useState, useCallback, useRef, useMemo } from 'react'
import { usePeopleNudges } from '@/resurfacing'
import {
  REFLECTION_PROMPTS,
  RESTART_PROMPTS,
  type ReflectionPrompt,
} from '@/entries'
import { useDocumentView } from '@/document'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HISTORY_KEY = 'reflection-prompt-history'
const DISMISSED_KEY = 'reflection-prompt-dismissed'
const ENGAGEMENT_START_KEY = 'reflection-engagement-start'
const MAX_HISTORY = 30
const RECENCY_DAYS = 7

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EngagementLevel = 'new' | 'light' | 'medium' | 'deep'

interface PromptHistoryEntry {
  promptId: string
  usedAt: string
}

export interface ReflectionPromptResult {
  prompt: ReflectionPrompt | null
  nextPrompt: () => void
  dismiss: () => void
  isDismissed: boolean
  isGapReturn: boolean
}

// ---------------------------------------------------------------------------
// Pure Selection Function (testable, no React dependencies)
// ---------------------------------------------------------------------------

// Exported for `__tests__/use-reflection-prompt.test.ts`, which names it 17 times and is the only
// caller outside this module (#428). The file is inside `entries/journal/` and `@/entries` does not
// publish the name, so the keyword widens no face.
export function selectPrompt(params: {
  prompts: ReflectionPrompt[]
  recentPromptIds: string[]
  currentHour: number
  hasOverdueContacts: boolean
  hasCompletedTasksToday: boolean
  daysSinceLastJournal: number
  totalJournalEntries: number
  engagementLevel: EngagementLevel
  skipIds?: string[]
}): ReflectionPrompt | null {
  const {
    prompts,
    recentPromptIds,
    currentHour,
    hasOverdueContacts,
    hasCompletedTasksToday,
    daysSinceLastJournal,
    totalJournalEntries,
    engagementLevel,
    skipIds = [],
  } = params

  // 1. Gap detection: 3+ days → restart prompt (only for returning users)
  if (daysSinceLastJournal >= 3 && totalJournalEntries > 0) {
    const available = RESTART_PROMPTS.filter((p) => !skipIds.includes(p.id))
    if (available.length === 0) return null
    return available[Math.floor(Math.random() * available.length)] ?? null
  }

  let candidates = [...prompts]

  // 2. Filter by time of day
  if (currentHour < 12) {
    // Morning: morning + any
    candidates = candidates.filter(
      (p) => p.timeOfDay === 'morning' || p.timeOfDay === 'any',
    )
  } else if (currentHour >= 17) {
    // Evening: evening + any
    candidates = candidates.filter(
      (p) => p.timeOfDay === 'evening' || p.timeOfDay === 'any',
    )
  } else {
    // Midday: any only
    candidates = candidates.filter((p) => p.timeOfDay === 'any')
  }

  // 3. Filter by depth (engagement level)
  switch (engagementLevel) {
    case 'new':
      candidates = candidates.filter((p) => p.depth === 'light')
      break
    case 'light':
      candidates = candidates.filter(
        (p) => p.depth === 'light' || p.depth === 'medium',
      )
      break
    case 'medium':
    case 'deep':
      // All depths allowed
      break
    default: {
      const _exhaustive: never = engagementLevel
      void _exhaustive
    }
  }

  // 4. Enforce context requirements
  if (!hasOverdueContacts) {
    candidates = candidates.filter((p) => !p.requiresPeople)
  }
  if (!hasCompletedTasksToday) {
    candidates = candidates.filter((p) => !p.requiresCompletedTasks)
  }

  // 5. Exclude recents
  candidates = candidates.filter((p) => !recentPromptIds.includes(p.id))

  // 6. Exclude skipIds
  candidates = candidates.filter((p) => !skipIds.includes(p.id))

  if (candidates.length === 0) return null

  // 6. Contextual priority (50% chance to pick from boosted category)
  if (hasOverdueContacts && Math.random() < 0.5) {
    const peopleCandidates = candidates.filter(
      (p) => p.category === 'people-connection',
    )
    if (peopleCandidates.length > 0) {
      return (
        peopleCandidates[Math.floor(Math.random() * peopleCandidates.length)] ??
        null
      )
    }
  }

  if (hasCompletedTasksToday && Math.random() < 0.5) {
    const accomplishCandidates = candidates.filter(
      (p) => p.category === 'accomplishment',
    )
    if (accomplishCandidates.length > 0) {
      return (
        accomplishCandidates[
          Math.floor(Math.random() * accomplishCandidates.length)
        ] ?? null
      )
    }
  }

  // 7. Category balance: prefer categories not in recents
  const recentCategories = new Set(
    prompts
      .filter((p) => recentPromptIds.includes(p.id))
      .map((p) => p.category),
  )

  const freshCategoryCandidates = candidates.filter(
    (p) => !recentCategories.has(p.category),
  )

  const pool =
    freshCategoryCandidates.length > 0 ? freshCategoryCandidates : candidates

  // 8. Random from candidates
  return pool[Math.floor(Math.random() * pool.length)] ?? null
}

// ---------------------------------------------------------------------------
// localStorage Helpers
// ---------------------------------------------------------------------------

function readHistory(): PromptHistoryEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed as PromptHistoryEntry[]
  } catch {
    return []
  }
}

function getRecentPromptIds(): string[] {
  const history = readHistory()
  const cutoff = Date.now() - RECENCY_DAYS * 24 * 60 * 60 * 1000
  return history
    .filter((h) => new Date(h.usedAt).getTime() > cutoff)
    .map((h) => h.promptId)
}

function isDismissedToday(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    if (!raw) return false
    const dismissedDate = new Date(raw).toDateString()
    return dismissedDate === new Date().toDateString()
  } catch {
    return false
  }
}

function dismissForToday(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString())
  } catch {
    // localStorage errors are non-critical
  }
}

export function recordPromptUsed(promptId: string): void {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    const history: PromptHistoryEntry[] = raw
      ? (JSON.parse(raw) as PromptHistoryEntry[])
      : []
    history.unshift({ promptId, usedAt: new Date().toISOString() })
    localStorage.setItem(
      HISTORY_KEY,
      JSON.stringify(history.slice(0, MAX_HISTORY)),
    )
  } catch {
    // localStorage errors are non-critical
  }
}

function getEngagementLevel(
  totalJournalEntries: number,
  engagementStartDate: string | null,
): EngagementLevel {
  if (totalJournalEntries < 3) return 'new'

  if (!engagementStartDate) return 'light'

  const daysSinceStart = Math.floor(
    (Date.now() - new Date(engagementStartDate).getTime()) /
      (1000 * 60 * 60 * 24),
  )

  if (daysSinceStart < 7) return 'light'
  if (daysSinceStart < 30) return 'medium'
  return 'deep'
}

function getEngagementStart(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(ENGAGEMENT_START_KEY)
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useReflectionPrompt(): ReflectionPromptResult {
  const doc = useDocumentView()
  const { overdueContacts } = usePeopleNudges()

  const skipIdsRef = useRef<string[]>([])
  const [promptVersion, setPromptVersion] = useState(0)
  const [dismissed, setDismissed] = useState(() => isDismissedToday())

  // Derive context from doc
  const context = useMemo(() => {
    if (!doc) {
      return {
        daysSinceLastJournal: 0,
        totalJournalEntries: 0,
        hasCompletedTasksToday: false,
      }
    }

    const entries = Object.values(doc.entries)
    const journalEntries = entries
      .filter((e) => e.type === 'journal' && !e.archivedAt)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )

    const totalJournalEntries = journalEntries.length

    let daysSinceLastJournal = 0
    if (journalEntries.length > 0 && journalEntries[0]) {
      const lastDate = new Date(journalEntries[0].createdAt)
      daysSinceLastJournal = Math.floor(
        (Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24),
      )
    } else {
      // No journal entries — treat as gap
      daysSinceLastJournal = 999
    }

    // Check for tasks completed today
    const todayStr = new Date().toDateString()
    const hasCompletedTasksToday = entries.some((e) => {
      if (e.type !== 'task') return false
      const completedAt = e.metadata.completedAt
      if (typeof completedAt !== 'string') return false
      return new Date(completedAt).toDateString() === todayStr
    })

    return { daysSinceLastJournal, totalJournalEntries, hasCompletedTasksToday }
  }, [doc])

  // Select prompt
  const { prompt, isGapReturn } = useMemo(() => {
    if (dismissed) return { prompt: null, isGapReturn: false }

    const recentPromptIds = getRecentPromptIds()
    const engagementStart = getEngagementStart()
    const engagementLevel = getEngagementLevel(
      context.totalJournalEntries,
      engagementStart,
    )

    const isGap = context.daysSinceLastJournal >= 3

    const selected = selectPrompt({
      prompts: REFLECTION_PROMPTS,
      recentPromptIds,
      currentHour: new Date().getHours(),
      hasOverdueContacts: overdueContacts.length > 0,
      hasCompletedTasksToday: context.hasCompletedTasksToday,
      daysSinceLastJournal: context.daysSinceLastJournal,
      totalJournalEntries: context.totalJournalEntries,
      engagementLevel,
      skipIds: skipIdsRef.current,
    })

    return { prompt: selected, isGapReturn: isGap }
    // promptVersion triggers re-selection when nextPrompt is called
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed, context, overdueContacts, promptVersion])

  const nextPrompt = useCallback(() => {
    if (prompt) {
      skipIdsRef.current = [...skipIdsRef.current, prompt.id]
    }
    setPromptVersion((v) => v + 1)
  }, [prompt])

  const dismiss = useCallback(() => {
    dismissForToday()
    setDismissed(true)
  }, [])

  return {
    prompt,
    nextPrompt,
    dismiss,
    isDismissed: dismissed,
    isGapReturn,
  }
}
